import React, { useState, useEffect, useMemo } from 'react';
import { useUser } from '@/contexts/UserContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/use-toast';
import { RefreshCw } from 'lucide-react';
import { canAccessDiambraTenant, canAccessEvasysTenant, canAccessLearnwiseTenant, canAccessCalmedicaTenant, canAccessDemarqueTenant, canAccessBibliothecaTenant, canAccessNiryoTenant, canAccessFunidataTenant, canAccessAkariTenant, canAccessAlmentorTenant, canAccessGravytyTenant, canAccessInfoneticaTenant, canAccessCayuseTenant, canAccessUplannerTenant, canAccessEdusignTenant, canAccessLiaisonTenant, canAccessSebaTenant, canAccessRumiTenant, canAccessStudentcrmTenant, canAccessUnit4Tenant, canAccessBlackboardTenant, canAccessConstructorTenant, canAccessEfektaTenant, canAccessJpikTenant, canAccessTestweTenant, canAccessBlackbullionTenant, canAccessWooclapTenant, canAccessPolleverywhereTenant, canAccessPositivoTenant, canAccessDreamapplyTenant, canAccess4scienceTenant, canAccessFeedbackfruitsTenant, canAccessLegacyFeedbackfruitsTenant, canAccessLegacyDemarqueTenant, canAccessFullfabricTenant, canAccessLegacyFullfabricTenant, canAccessSurpassTenant, canAccessLegacySurpassTenant, isCalmedicaEmail, isFeedbackfruitsEmail, isDemarqueEmail, isFullfabricEmail, isSurpassEmail, normalizeTenantId, isEvasysFamilyTenant, isFeedbackfruitsFamilyTenant, isDemarqueFamilyTenant, isBibliothecaFamilyTenant, isFullfabricFamilyTenant, isSurpassFamilyTenant, isLegacyFeedbackfruitsTenant, isLegacyDemarqueTenant, isLegacyFullfabricTenant, isLegacySurpassTenant } from '@/lib/tenantUtils';

const ALL_TENANTS = [
  { id: 'instructure', label: 'Instructure (Default)' },
  { id: 'clairelabs', label: 'Claire Labs' },
  { id: 'demarque', label: 'Demarque' },
  { id: 'demarque_V2', label: 'Demarque V2' },
  { id: 'bibliotheca', label: 'Bibliotheca' },
  { id: 'smowl', label: 'Smowl' },
  { id: 'surpass', label: 'Surpass' },
  { id: 'surpass_V2', label: 'Surpass V2' },
  { id: 'vretta', label: 'Vretta' },
  { id: 'diambra', label: 'Diambra' },
  { id: 'edqwest', label: 'EdQwest' },
  { id: 'calmedica', label: 'Calmedica' },
  { id: 'faria', label: 'Faria' },
  { id: 'sebit', label: 'Sebit' },
  { id: 'prowise', label: 'Prowise' },
  { id: 'skillwell', label: 'Skillwell' },
  { id: 'kairadigital', label: 'Kaira Digital' },
  { id: 'wayground', label: 'Wayground' },
  { id: 'plataformaa', label: 'Plataformaa' },
  { id: 'evasys', label: 'Evasys' },
  { id: 'evasys_V2', label: 'Evasys V2' },
  { id: 'synap', label: 'Synap' },
  { id: 'scantron', label: 'Scantron' },
  { id: 'learnwise', label: 'Learnwise' },
  { id: 'funidata', label: 'Funidata' },
  { id: 'akari', label: 'Akari' },
  { id: 'almentor', label: 'Almentor' },
  { id: 'gravyty', label: 'Gravyty' },
  { id: 'infonetica', label: 'Infonetica' },
  { id: 'cayuse', label: 'Cayuse' },
  { id: 'uplanner', label: 'uPlanner' },
  { id: 'edusign', label: 'Edusign' },
  { id: 'liaison', label: 'Liaison' },
  { id: 'fullfabric', label: 'Full Fabric' },
  { id: 'fullfabric_V2', label: 'Full Fabric V2' },
  { id: 'seba', label: 'Seba' },
  { id: 'rumi', label: 'Rumi' },
  { id: 'studentcrm', label: 'StudentCRM' },
  { id: 'unit4', label: 'Unit4' },
  { id: 'blackboard', label: 'Blackboard' },
  { id: 'terminalfour', label: 'Terminalfour' },
  { id: 'constructor_tech', label: 'Constructor' },
  { id: 'efekta', label: 'Efekta' },
  { id: 'jpik', label: 'JPIK' },
  { id: 'verypc', label: 'VeryPC' },
  // [2026-10-15] access_people — The Access Group's Access People division (PeopleXD HR &
  // payroll, Access Financials). BE lens profile 41, UK + Ireland. Left UNGATED like
  // terminalfour and verypc: the email-domain gates above exist for tenants whose own staff
  // log in, and this is a prospect Tiago demos. 🔴 A tenant can be fully wired in the
  // database — entitlement, lens, admin_atoms, scores, personas — and still be unreachable,
  // because THIS hardcoded list is what the switcher renders.
  { id: 'access_people', label: 'Access People' },
  // [2026-10-15] niryo — Niryo, the French educational robot-arm maker. BE lens profile 42
  // (robotics_automation_training), entitlement US + France + UK + Germany, higher ed only.
  // Left UNGATED like access_people and verypc: the email-domain gates above are for tenants whose
  // own staff log in, and this is a prospect Tiago demos. 🔴 A tenant can be fully wired in the
  // database — entitlement, lens, admin_atoms, scores, accounts — and still be unreachable,
  // because THIS hardcoded list is what the switcher renders.
  { id: 'niryo', label: 'Niryo' },
  // [2026-10-16] 4science — 4Science (DSpace-CRIS, DSpace, DSpace-GLAM). BE lens profile 43
  // (research_repository: CRIS, repositories, digital preservation), higher-ed entitlement cloned
  // from evasys_V2.
  // [2026-10-21] NOW GATED (Tiago): its own staff log in on @4science.com, so only they and
  // superadmins may open it. Was UNGATED as a prospect Tiago demos — superadmin still reaches it.
  { id: '4science', label: '4Science' },
  // [2026-10-18] testwe — TestWe (online exams, ProctorWe, AI Assistant). BE lens profile 44
  // (online_exams: online_exams + proctoring + marking); entitlement = surpass_V2 ∪ feedbackfruits_V2
  // ∪ the accredited business-school market. GATED like constructor_tech: its own staff log in on
  // @testwe.eu, so only they and superadmins may open it.
  { id: 'testwe', label: 'TestWe' },
  // [2026-10-19] dreamapply — DreamApply (international admissions / application management CRM).
  // BE lens admissions_crm; countries = fullfabric_V2's set minus Brazil; sectors = higher ed +
  // business schools. Single product (no lanes). GATED like testwe and constructor_tech: its own
  // staff log in on @dreamapply.com, so only they and superadmins may open it.
  { id: 'dreamapply', label: 'DreamApply' },
  // [2026-10-22] blackbullion — Blackbullion (blackbullion.com): student financial wellbeing,
  // hardship and scholarship fund administration, and financial-risk identification for retention.
  // BE lens profile 46 (`student_success`, one category); entitlement UK + Ireland + United States,
  // higher ed only. SINGLE PRODUCT, no lanes, and `admin_atoms.products` is [] — Tiago,
  // 2026-10-22: "assume no products, just use a company profile".
  // [2026-10-29] GATED like testwe (Tiago: set up @blackbullion.com "properly" for this tenant) —
  // @blackbullion.com users or superadmin only, via canAccessBlackbullionTenant. Was left ungated as
  // a demo prospect; its own staff now sign in. BE maps blackbullion.com -> blackbullion.
  { id: 'blackbullion', label: 'Blackbullion' },
  // [2026-10-31] wooclap — Wooclap (wooclap.com): live polling, audience response and active learning
  // for teaching (Wooclap, Wooflash; acquired Vevox 2026-09-15). BE lens `polling`, one category;
  // 12 countries, higher ed incl. UK FE + Quebec CEGEPs. SINGLE PRODUCT (no lanes). GATED like
  // testwe/blackbullion (Tiago, 2026-10-31): @wooclap.com users or superadmin only, via
  // canAccessWooclapTenant. BE maps wooclap.com -> wooclap.
  { id: 'wooclap', label: 'Wooclap' },
  // [2026-11-02] polleverywhere — Poll Everywhere (polleverywhere.com): live polling and audience response for teaching.
  // BE lens `polling`, entitlement copied from wooclap. SINGLE PRODUCT. GATED like wooclap (Tiago, 2026-11-02):
  // @polleverywhere.com users or superadmin only, via canAccessPolleverywhereTenant. BE maps polleverywhere.com.
  { id: 'polleverywhere', label: 'Poll Everywhere' },
  // [2026-11-03] positivo — Positivo Tecnologia (positivo.com.br): student and teacher devices for school systems.
  // BE lens it_hardware, market copied from jpik. SINGLE PRODUCT. GATED like polleverywhere: @positivo.com.br users or
  // superadmin only, via canAccessPositivoTenant. BE maps positivo.com.br.
  { id: 'positivo', label: 'Positivo' },
  { id: 'feedbackfruits', label: 'FeedbackFruits' },
  { id: 'feedbackfruits_V2', label: 'FeedbackFruits V2' },
  { id: 'global', label: 'Global Data' },
];

/** Still on the platform; omitted from the admin tenant dropdown unless currently selected. */
const HIDDEN_FROM_ADMIN_SWITCHER = new Set([
  'faria',
  'edqwest',
  'diambra',
  'clairelabs',
  'kairadigital',
  'uplanner',
  'plataformaa',
  'wayground',
  'fullfabric',
  'global',
  'demarque',
  // [2026-10-19] the legacy ids, hidden on instruction — the _V2 rows stay visible.
  // Same treatment already given to `fullfabric` and `demarque` above.
  'feedbackfruits',
  'surpass',
]);

function resolveStoredTenant(stored, user) {
  const s = normalizeTenantId(stored);
  if (s === 'diambra' && !canAccessDiambraTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (isEvasysFamilyTenant(s) && !canAccessEvasysTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'learnwise' && !canAccessLearnwiseTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'calmedica' && !canAccessCalmedicaTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (isDemarqueFamilyTenant(s) && !canAccessDemarqueTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (isBibliothecaFamilyTenant(s) && !canAccessBibliothecaTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  // [2026-10-28] niryo is @niryo.com (or admin) only — same as bibliotheca above.
  if (s === 'niryo' && !canAccessNiryoTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (isLegacyDemarqueTenant(s) && !canAccessLegacyDemarqueTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return isDemarqueEmail(user?.email) ? 'demarque_V2' : (normalizeTenantId(user?.tenant_id) || 'instructure');
  }
  if (s === 'funidata' && !canAccessFunidataTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'akari' && !canAccessAkariTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'almentor' && !canAccessAlmentorTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'gravyty' && !canAccessGravytyTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'infonetica' && !canAccessInfoneticaTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'cayuse' && !canAccessCayuseTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'uplanner' && !canAccessUplannerTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'edusign' && !canAccessEdusignTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'liaison' && !canAccessLiaisonTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'seba' && !canAccessSebaTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'rumi' && !canAccessRumiTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'studentcrm' && !canAccessStudentcrmTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'unit4' && !canAccessUnit4Tenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'blackboard' && !canAccessBlackboardTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'constructor_tech' && !canAccessConstructorTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'testwe' && !canAccessTestweTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'blackbullion' && !canAccessBlackbullionTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'wooclap' && !canAccessWooclapTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'polleverywhere' && !canAccessPolleverywhereTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'positivo' && !canAccessPositivoTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  // [2026-10-21] dreamapply and 4science — without these, a stored admin-active-tenant
  // survives in localStorage for a user who may no longer open that tenant.
  if (s === 'dreamapply' && !canAccessDreamapplyTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === '4science' && !canAccess4scienceTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'efekta' && !canAccessEfektaTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (s === 'jpik' && !canAccessJpikTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (isFeedbackfruitsFamilyTenant(s) && !canAccessFeedbackfruitsTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (isLegacyFeedbackfruitsTenant(s) && !canAccessLegacyFeedbackfruitsTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return isFeedbackfruitsEmail(user?.email) ? 'feedbackfruits_V2' : (normalizeTenantId(user?.tenant_id) || 'instructure');
  }
  if (isFullfabricFamilyTenant(s) && !canAccessFullfabricTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (isLegacyFullfabricTenant(s) && !canAccessLegacyFullfabricTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return isFullfabricEmail(user?.email) ? 'fullfabric_V2' : (normalizeTenantId(user?.tenant_id) || 'instructure');
  }
  if (isSurpassFamilyTenant(s) && !canAccessSurpassTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return normalizeTenantId(user?.tenant_id) || 'instructure';
  }
  if (isLegacySurpassTenant(s) && !canAccessLegacySurpassTenant(user)) {
    localStorage.removeItem('admin-active-tenant');
    return isSurpassEmail(user?.email) ? 'surpass_V2' : (normalizeTenantId(user?.tenant_id) || 'instructure');
  }
  // @calmedica.com users: always use calmedica, ignore stored
  if (isCalmedicaEmail(user?.email)) return 'calmedica';
  return s || normalizeTenantId(user?.tenant_id) || 'instructure';
}

export default function TenantSwitcher({ currentUser: propsUser, onTenantChange, className }) {
  const contextUser = useUser();
  const currentUser = propsUser || contextUser;
  const [activeTenant, setActiveTenant] = useState('instructure');
  const [isLoading, setIsLoading] = useState(!propsUser);
  const { toast } = useToast();

  const availableTenants = useMemo(() => {
    const canDiambra = canAccessDiambraTenant(currentUser);
    const canEvasys = canAccessEvasysTenant(currentUser);
    const canLearnwise = canAccessLearnwiseTenant(currentUser);
    const canCalmedica = canAccessCalmedicaTenant(currentUser);
    const canDemarque = canAccessDemarqueTenant(currentUser);
    const canBibliotheca = canAccessBibliothecaTenant(currentUser);
    const canFunidata = canAccessFunidataTenant(currentUser);
    const canAkari = canAccessAkariTenant(currentUser);
    const canAlmentor = canAccessAlmentorTenant(currentUser);
    const canGravyty = canAccessGravytyTenant(currentUser);
    const canInfonetica = canAccessInfoneticaTenant(currentUser);
    const canCayuse = canAccessCayuseTenant(currentUser);
    const canUplanner = canAccessUplannerTenant(currentUser);
    const canEdusign = canAccessEdusignTenant(currentUser);
    const canLiaison = canAccessLiaisonTenant(currentUser);
    const canSeba = canAccessSebaTenant(currentUser);
    const canRumi = canAccessRumiTenant(currentUser);
    const canStudentcrm = canAccessStudentcrmTenant(currentUser);
    const canUnit4 = canAccessUnit4Tenant(currentUser);
    const canBlackboard = canAccessBlackboardTenant(currentUser);
    const canConstructor = canAccessConstructorTenant(currentUser);
    const canTestwe = canAccessTestweTenant(currentUser);
    const canEfekta = canAccessEfektaTenant(currentUser);
    const canJpik = canAccessJpikTenant(currentUser);
    const canFeedbackfruits = canAccessFeedbackfruitsTenant(currentUser);
    const canFullfabric = canAccessFullfabricTenant(currentUser);
    const canSurpass = canAccessSurpassTenant(currentUser);
    const isCalmedica = isCalmedicaEmail(currentUser?.email);
    return ALL_TENANTS.filter(t => {
      // Hidden from the chooser, but keep the active value so Radix Select stays valid.
      if (HIDDEN_FROM_ADMIN_SWITCHER.has(t.id) && t.id !== activeTenant) return false;
      if (t.id === 'diambra') return canDiambra;
      if (t.id === 'evasys' || t.id === 'evasys_V2') return canEvasys;
      if (t.id === 'learnwise') return canLearnwise;
      if (t.id === 'calmedica') return canCalmedica;
      if (t.id === 'demarque') return canAccessLegacyDemarqueTenant(currentUser);
      if (t.id === 'demarque_V2') return canDemarque;
      if (t.id === 'bibliotheca') return canBibliotheca;
      if (t.id === 'niryo') return canAccessNiryoTenant(currentUser);
      if (t.id === 'funidata') return canFunidata;
      if (t.id === 'akari') return canAkari;
      if (t.id === 'almentor') return canAlmentor;
      if (t.id === 'gravyty') return canGravyty;
      if (t.id === 'infonetica') return canInfonetica;
      if (t.id === 'cayuse') return canCayuse;
      if (t.id === 'uplanner') return canUplanner;
      if (t.id === 'edusign') return canEdusign;
      if (t.id === 'liaison') return canLiaison;
      if (t.id === 'seba') return canSeba;
      if (t.id === 'rumi') return canRumi;
      if (t.id === 'studentcrm') return canStudentcrm;
      if (t.id === 'unit4') return canUnit4;
      if (t.id === 'blackboard') return canBlackboard;
      if (t.id === 'constructor_tech') return canConstructor;
      if (t.id === 'testwe') return canTestwe;
      if (t.id === 'blackbullion') return canAccessBlackbullionTenant(currentUser);
      if (t.id === 'wooclap') return canAccessWooclapTenant(currentUser);
      if (t.id === 'polleverywhere') return canAccessPolleverywhereTenant(currentUser);
      if (t.id === 'positivo') return canAccessPositivoTenant(currentUser);
      if (t.id === 'dreamapply') return canAccessDreamapplyTenant(currentUser);
      if (t.id === '4science') return canAccess4scienceTenant(currentUser);
      if (t.id === 'efekta') return canEfekta;
      if (t.id === 'jpik') return canJpik;
      if (t.id === 'feedbackfruits') return canAccessLegacyFeedbackfruitsTenant(currentUser);
      if (t.id === 'feedbackfruits_V2') return canFeedbackfruits;
      if (t.id === 'fullfabric') return canAccessLegacyFullfabricTenant(currentUser);
      if (t.id === 'fullfabric_V2') return canFullfabric;
      if (t.id === 'surpass') return canAccessLegacySurpassTenant(currentUser);
      if (t.id === 'surpass_V2') return canSurpass;
      // @calmedica.com users: only show calmedica (hide other tenants)
      if (isCalmedica) return t.id === 'calmedica';
      return true;
    }).sort((a, b) => a.label.localeCompare(b.label));
  }, [currentUser, activeTenant]);

  useEffect(() => {
    const user = propsUser || contextUser;
    if (user) {
      const stored =
        user?.role === 'admin'
          ? localStorage.getItem('admin-active-tenant')
          : null;
      setActiveTenant(resolveStoredTenant(stored, user));
    }
    setIsLoading(false);
  }, [propsUser, contextUser]);

  const handleTenantChange = (newTenant) => {
    if (!currentUser || currentUser.role !== 'admin') {
      toast({ title: 'Access denied', description: 'Only admins can switch tenants.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'diambra' && !canAccessDiambraTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Diambra tenant requires an @diambra.ai or @ungaii.co email.', variant: 'destructive' });
      return;
    }
    if (isEvasysFamilyTenant(newTenant) && !canAccessEvasysTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Evasys tenant requires an @evasys.de email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'learnwise' && !canAccessLearnwiseTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Learnwise tenant requires an @learnwise.ai email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'calmedica' && !canAccessCalmedicaTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Calmedica tenant requires an @calmedica.com email.', variant: 'destructive' });
      return;
    }
    if (isDemarqueFamilyTenant(newTenant) && !canAccessDemarqueTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Demarque tenant requires an @demarque.com email.', variant: 'destructive' });
      return;
    }
    if (isBibliothecaFamilyTenant(newTenant) && !canAccessBibliothecaTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Bibliotheca tenant requires an @bibliotheca.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'niryo' && !canAccessNiryoTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Niryo tenant requires an @niryo.com email.', variant: 'destructive' });
      return;
    }
    if (isLegacyDemarqueTenant(newTenant) && !canAccessLegacyDemarqueTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Demarque employees use the V2 workspace only.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'funidata' && !canAccessFunidataTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Funidata tenant requires an @funidata.fi email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'akari' && !canAccessAkariTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Akari tenant requires an @akarisoftware.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'almentor' && !canAccessAlmentorTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Almentor tenant requires an @almentor.net email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'gravyty' && !canAccessGravytyTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Gravyty tenant requires an @gravyty.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'infonetica' && !canAccessInfoneticaTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Infonetica tenant requires an @infonetica.net email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'cayuse' && !canAccessCayuseTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Cayuse tenant requires an @cayuse.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'uplanner' && !canAccessUplannerTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'uPlanner tenant requires an @uplanner.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'edusign' && !canAccessEdusignTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Edusign tenant requires an @edusign.fr email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'liaison' && !canAccessLiaisonTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Liaison tenant requires an @liaisonedu.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'seba' && !canAccessSebaTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Seba tenant requires a @turktelekom.com.tr, @argela.com.tr, or @netsia.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'rumi' && !canAccessRumiTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Rumi tenant requires an @rumiessay.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'studentcrm' && !canAccessStudentcrmTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'StudentCRM tenant requires an @student-crm.co.uk email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'unit4' && !canAccessUnit4Tenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Unit4 tenant requires an @unit4.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'blackboard' && !canAccessBlackboardTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Blackboard tenant requires an @blackboard.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'constructor_tech' && !canAccessConstructorTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Constructor tenant requires an @constructor.tech email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'testwe' && !canAccessTestweTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'TestWe tenant requires an @testwe.eu email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'blackbullion' && !canAccessBlackbullionTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Blackbullion tenant requires an @blackbullion.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'wooclap' && !canAccessWooclapTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Wooclap tenant requires an @wooclap.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'polleverywhere' && !canAccessPolleverywhereTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Poll Everywhere tenant requires an @polleverywhere.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'positivo' && !canAccessPositivoTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Positivo tenant requires an @positivo.com.br email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'dreamapply' && !canAccessDreamapplyTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'DreamApply tenant requires an @dreamapply.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === '4science' && !canAccess4scienceTenant(currentUser)) {
      toast({ title: 'Access denied', description: '4Science tenant requires an @4science.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'efekta' && !canAccessEfektaTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Efekta tenant requires an @efekta.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'jpik' && !canAccessJpikTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'JPIK tenant requires an @jpik.com email.', variant: 'destructive' });
      return;
    }
    if (isFeedbackfruitsFamilyTenant(newTenant) && !canAccessFeedbackfruitsTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'FeedbackFruits tenant requires an @feedbackfruits.com email.', variant: 'destructive' });
      return;
    }
    if (isLegacyFeedbackfruitsTenant(newTenant) && !canAccessLegacyFeedbackfruitsTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'FeedbackFruits employees use the V2 workspace only.', variant: 'destructive' });
      return;
    }
    if (isFullfabricFamilyTenant(newTenant) && !canAccessFullfabricTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Full Fabric tenant requires an @fullfabric.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'fullfabric' && !canAccessLegacyFullfabricTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Full Fabric employees use the V2 workspace only.', variant: 'destructive' });
      return;
    }
    if (isSurpassFamilyTenant(newTenant) && !canAccessSurpassTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Surpass tenant requires an @surpass.com email.', variant: 'destructive' });
      return;
    }
    if (newTenant === 'surpass' && !canAccessLegacySurpassTenant(currentUser)) {
      toast({ title: 'Access denied', description: 'Surpass employees use the V2 workspace only.', variant: 'destructive' });
      return;
    }
    setActiveTenant(newTenant);
    localStorage.setItem('admin-active-tenant', newTenant);
    const label = ALL_TENANTS.find(t => t.id === newTenant)?.label;
    toast({ title: 'Tenant Switched', description: `Now viewing data for: ${label}` });
    if (onTenantChange) onTenantChange(newTenant);
    else setTimeout(() => window.location.reload(), 500);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 text-sm text-gray-500 ${className || ''}`}>
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span className="hidden md:inline">Loading...</span>
      </div>
    );
  }

  // Only show for admin users
  if (!currentUser || currentUser.role !== 'admin') {
    console.log('TenantSwitcher: Not showing - user role:', currentUser?.role);
    return null;
  }

  console.log('TenantSwitcher: Showing for admin user:', currentUser.email, 'Active tenant:', activeTenant);

  return (
    <Select value={activeTenant} onValueChange={handleTenantChange}>
      <SelectTrigger
        className={`
          w-32 md:w-40 h-8 text-xs
          bg-transparent border-none shadow-none
          text-gray-200
          focus:outline-none focus-visible:outline-none
          focus:ring-0 focus-visible:ring-0
          hover:bg-transparent
          ${className || ''}
        `}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {availableTenants.map(tenant => (
          <SelectItem key={tenant.id} value={tenant.id} className="text-xs">
            {tenant.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}